import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, ArrowLeft, Car as CarIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const BODY_TYPES = [
  { value: "sedan", label: "Sedan" },
  { value: "coupe", label: "Coupe" },
  { value: "hatchback", label: "Hatchback" },
  { value: "wagon", label: "Wagon" },
  { value: "suv-2row", label: "SUV (2 rows)" },
  { value: "suv-3row", label: "SUV (3 rows)" },
  { value: "minivan", label: "Minivan" },
  { value: "pickup", label: "Pickup Truck" },
  { value: "convertible", label: "Convertible" },
];

function Autocomplete({ value, onChange, options, placeholder, testid, onPick }) {
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const q = (value || "").toLowerCase().trim();
    if (!q) return options.slice(0, 50);
    return options.filter((o) => o.toLowerCase().includes(q)).slice(0, 50);
  }, [value, options]);

  return (
    <div className="relative">
      <input
        className="input-luxe"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        data-testid={testid}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <ul
          className="absolute top-full left-0 right-0 z-40 mt-1 max-h-72 overflow-y-auto bg-ink-paper border border-gold/30"
          style={{ background: "#0A0A0A" }}
        >
          {filtered.map((opt) => (
            <li
              key={opt}
              className="px-4 py-2.5 cursor-pointer text-sm text-white hover:bg-elevated hover:text-gold border-b border-white/5 last:border-0"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(opt);
                if (onPick) onPick(opt);
                setOpen(false);
              }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CarStep({ value, onChange, onNext, onBack }) {
  const [make, setMake] = useState(value?.make || "");
  const [model, setModel] = useState(value?.model || "");
  const [year, setYear] = useState(value?.year || "");
  const [bodyType, setBodyType] = useState(value?.body_type || "");
  const [isCustom, setIsCustom] = useState(value?.is_custom || false);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingMakes, setLoadingMakes] = useState(true);
  const [popularMakes, setPopularMakes] = useState([]);
  const [popularModels, setPopularModels] = useState([]);

  // Popular makes that auto-fill (curated from NHTSA database for our area)
  const POPULAR_MAKES = [
    "Honda", "Toyota", "Ford", "Chevrolet", "Subaru",
    "Jeep", "BMW", "Mercedes-Benz", "Audi", "Tesla",
    "Hyundai", "Kia", "Mazda", "Nissan", "Volkswagen",
  ];

  // Popular models for the most common makes
  const POPULAR_MODELS = {
    Honda: ["Civic", "Accord", "CR-V", "Pilot", "Odyssey"],
    Toyota: ["Camry", "Corolla", "RAV4", "Highlander", "Tacoma"],
    Ford: ["F-150", "Escape", "Explorer", "Mustang", "Bronco"],
    Chevrolet: ["Silverado 1500", "Equinox", "Tahoe", "Malibu", "Traverse"],
    Subaru: ["Outback", "Forester", "Crosstrek", "Impreza", "WRX"],
    Jeep: ["Wrangler", "Grand Cherokee", "Cherokee", "Gladiator"],
    BMW: ["3 Series", "5 Series", "X3", "X5", "X7"],
    "Mercedes-Benz": ["C-Class", "E-Class", "GLC", "GLS-Class"],
    Audi: ["A4", "Q5", "Q7", "A6"],
    Tesla: ["Model 3", "Model Y", "Model S", "Model X"],
    Hyundai: ["Elantra", "Sonata", "Tucson", "Santa Fe", "Palisade"],
    Kia: ["Forte", "K5", "Sportage", "Sorento", "Telluride"],
    Mazda: ["CX-5", "CX-9", "Mazda3", "CX-30"],
    Nissan: ["Altima", "Sentra", "Rogue", "Pathfinder", "Frontier"],
    Volkswagen: ["Jetta", "Passat", "Tiguan", "Atlas", "Golf"],
  };

  useEffect(() => {
    setLoadingMakes(true);
    api
      .get("/cars/makes")
      .then(({ data }) => {
        const allMakes = data.makes || [];
        setMakes(allMakes);
        // Auto-fill popular makes that exist in the database
        const popular = POPULAR_MAKES.filter((m) => allMakes.includes(m));
        setPopularMakes(popular);
      })
      .catch(() => {})
      .finally(() => setLoadingMakes(false));
  }, []);

  useEffect(() => {
    if (!make || isCustom) return;
    if (!makes.includes(make)) return; // wait until exact match
    setLoadingModels(true);
    api
      .get("/cars/models", { params: { make } })
      .then(({ data }) => {
        const dbModels = data.models || [];
        setModels(dbModels);
        // Auto-fill popular models that exist in the database
        const popular = (POPULAR_MODELS[make] || []).filter((m) =>
          dbModels.includes(m)
        );
        setPopularModels(popular);
      })
      .catch(() => {
        setModels([]);
        setPopularModels([]);
      })
      .finally(() => setLoadingModels(false));
  }, [make, makes, isCustom]);

  useEffect(() => {
    if (!make || !model || isCustom) return;
    // Auto-detect body type
    api
      .get("/cars/body-type", { params: { make, model } })
      .then(({ data }) => {
        if (data.body_type && !bodyType) setBodyType(data.body_type);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [make, model, isCustom]);

  const submit = () => {
    if (isCustom) {
      if (!make || !model) {
        toast.error("Please enter make and model");
        return;
      }
      onChange({
        make,
        model,
        year: year ? parseInt(year, 10) : null,
        body_type: "suv-2row",
        is_custom: true,
      });
    } else {
      if (!make || !model) {
        toast.error("Please choose a make and model");
        return;
      }
      if (!bodyType) {
        toast.error("Please choose the body type");
        return;
      }
      onChange({
        make,
        model,
        year: year ? parseInt(year, 10) : null,
        body_type: bodyType,
        is_custom: false,
      });
    }
    onNext();
  };

  return (
    <div data-testid="step-car">
      <p className="overline mb-6">Step 02 · Your Vehicle</p>
      <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-[0.95] mb-4">
        Tell us about
        <br />
        <span className="gold-gradient-text italic">the canvas.</span>
      </h2>
      <p className="text-white/55 mb-10">
        We use the U.S. National Highway Traffic Safety Administration data for every make and model on the road.
      </p>

      <div className="flex items-center gap-2 mb-8">
        <button
          className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-all ${
            !isCustom ? "border-gold text-gold" : "border-white/15 text-white/50 hover:text-white"
          }`}
          onClick={() => setIsCustom(false)}
          data-testid="car-mode-known"
        >
          Choose from list
        </button>
        <button
          className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-all ${
            isCustom ? "border-gold text-gold" : "border-white/15 text-white/50 hover:text-white"
          }`}
          onClick={() => setIsCustom(true)}
          data-testid="car-mode-custom"
        >
          Enter Custom
        </button>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block mb-3 text-xs tracking-[0.25em] uppercase text-white/50">Make</label>
          {isCustom ? (
            <input
              className="input-luxe"
              value={make}
              onChange={(e) => setMake(e.target.value)}
              placeholder="e.g. Tesla"
              data-testid="car-make-input"
            />
          ) : (
            <Autocomplete
              value={make}
              onChange={(v) => {
                setMake(v);
                setModel("");
                setBodyType("");
              }}
              options={makes}
              placeholder="Start typing (Honda, Toyota…)"
              testid="car-make-input"
            />
          )}
          {!isCustom && (
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 flex items-center gap-1.5">
                <Sparkles size={10} className="text-gold" /> Quick pick from database
              </p>
              {loadingMakes ? (
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-7 w-20 rounded-full" />
                  ))}
                </div>
              ) : popularMakes.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {popularMakes.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        setMake(m);
                        setModel("");
                        setBodyType("");
                      }}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                        make === m
                          ? "border-gold text-gold bg-gold/5"
                          : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                      }`}
                      data-testid={`car-popular-make-${m}`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div>
          <label className="block mb-3 text-xs tracking-[0.25em] uppercase text-white/50">Model</label>
          {isCustom ? (
            <input
              className="input-luxe"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="e.g. Model Y"
              data-testid="car-model-input"
            />
          ) : (
            <Autocomplete
              value={model}
              onChange={(v) => {
                setModel(v);
                setBodyType("");
              }}
              options={models}
              placeholder={loadingModels ? "Loading models…" : "Start typing the model"}
              testid="car-model-input"
              onPick={() => {}}
            />
          )}
          {!isCustom && make && makes.includes(make) && (
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 flex items-center gap-1.5">
                <Sparkles size={10} className="text-gold" /> Popular {make} models
              </p>
              {loadingModels ? (
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-7 w-20 rounded-full" />
                  ))}
                </div>
              ) : popularModels.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {popularModels.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        setModel(m);
                        setBodyType("");
                      }}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                        model === m
                          ? "border-gold text-gold bg-gold/5"
                          : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                      }`}
                      data-testid={`car-popular-model-${m}`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div>
          <label className="block mb-3 text-xs tracking-[0.25em] uppercase text-white/50">Year (optional)</label>
          <input
            type="number"
            className="input-luxe"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="2022"
            min={1980}
            max={2030}
            data-testid="car-year-input"
          />
        </div>

        {!isCustom && (
          <div>
            <label className="block mb-3 text-xs tracking-[0.25em] uppercase text-white/50">
              Body type {bodyType && <span className="text-gold/70 normal-case tracking-normal ml-2">· auto-detected, confirm or change</span>}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {BODY_TYPES.map((b) => (
                <button
                  key={b.value}
                  onClick={() => setBodyType(b.value)}
                  className={`px-4 py-3 text-sm border transition-all ${
                    bodyType === b.value
                      ? "border-gold text-gold bg-gold/5"
                      : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                  }`}
                  data-testid={`car-body-${b.value}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {isCustom && (
          <div className="tile-spec p-4">
            <p className="text-xs text-gold/80 leading-relaxed">
              Custom vehicles are priced as a 2-row SUV by default. You can discuss adjustments with us at the appointment.
            </p>
          </div>
        )}
      </div>

      <div className="mt-12 flex justify-between">
        <button className="btn-secondary" onClick={onBack} data-testid="car-back-button">
          <ArrowLeft size={14} /> Back
        </button>
        <button className="btn-primary" onClick={submit} data-testid="car-next-button">
          Continue
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}
